/**
 * Grading and the pot (spec section 2).
 *
 * The push decision is only hard because probe difficulty rises faster than the
 * multiplier. Nothing here softens that without saying so out loud: there is
 * exactly ONE partial-credit rule in the game — the golden round's numeric
 * NEAR band — and it is argued for at length in `NUMERIC_SPECS`. Everywhere
 * else it is still no partial credit, no consolation points, no mercy on a
 * timeout.
 *
 * Rung 1 pays a base plus a linearly decaying speed bonus, SCALED BY THE
 * ATTEMPT MULTIPLIER (change 4), and every rung after multiplies the whole pot.
 */

import {
  SCORING,
  numericCreditBand,
  pinpointAttemptMultiplier,
  pinpointAttemptPolicy,
  pinpointAward,
  sealReveal,
} from '@geogame/shared';
import type {
  AnswerReveal,
  AnswerValue,
  LngLat,
  NumericCreditBand,
  PinpointAttemptPolicy,
  RevealBody,
  RungVerdict,
  Tier,
} from '@geogame/shared';
import { byCca3 } from '@geogame/data';

import type { PublicOption, PublicRung, RoundPlan, RungPlan } from './types';

/**
 * Cumulative pot after clearing `rung`, given the rung-1 award.
 *
 *   potAfter(1, 100) === 100
 *   potAfter(2, 100) === 200
 *   potAfter(3, 100) === 500
 *   potAfter(4, 100) === 1500
 *   potAfter(4, 100, { golden: true, ladderDepth: 4 }) === 4500
 *
 * The multipliers compound against the base, and the result is rounded once at
 * the end so a x2.5 on an odd base cannot leak fractional points into the
 * scoreboard.
 *
 * GOLDEN (change 6): when `opts.golden` is set, the TERMINAL rung of the
 * ladder — `opts.ladderDepth`, defaulting to `SCORING.MAX_RUNG` — is scaled by
 * `SCORING.GOLDEN_MULTIPLIER_FACTOR`. Expressing it as a factor on the
 * terminal rung rather than as a hardcoded x9 is what makes "triple what a
 * normal rung 4 pays" mean the same thing on Explorer's three-rung ladder,
 * where the terminal rung is 3 and the golden multiplier is x7.5.
 */
export function potAfter(
  rung: number,
  basePoints: number,
  opts?: { readonly golden?: boolean; readonly ladderDepth?: number },
): number {
  if (!Number.isInteger(rung) || rung < 1 || rung > SCORING.MAX_RUNG) {
    throw new RangeError(`potAfter: rung ${rung} outside 1..${SCORING.MAX_RUNG}`);
  }
  if (!Number.isFinite(basePoints)) {
    throw new RangeError(`potAfter: basePoints must be finite, got ${basePoints}`);
  }
  const terminal = opts?.ladderDepth ?? SCORING.MAX_RUNG;

  let multiplier = 1;
  for (let r = 2; r <= rung; r += 1) {
    const m = SCORING.RUNG_MULTIPLIERS[r];
    const base = typeof m === 'number' ? m : 1;
    multiplier *= opts?.golden === true && r === terminal ? base * SCORING.GOLDEN_MULTIPLIER_FACTOR : base;
  }
  return Math.round(basePoints * multiplier);
}

// ---------------------------------------------------------------------------
// Rung 1 — the pinpoint, now with attempts (change 4)
// ---------------------------------------------------------------------------

/**
 * How a pinpoint attempt went.
 *
 * `retry` is the field the caller must branch on. It is true exactly when the
 * tap was WRONG and the tier's allowance is not spent, which is the one state
 * in which the rung is still in play — and therefore the one state in which
 * the target may not be revealed (invariant 5). Everything else is resolved
 * and a reveal is owed (change 3).
 */
export interface PinpointVerdict {
  readonly correct: boolean;
  /** Award for this attempt, already scaled by the attempt multiplier. */
  readonly points: number;
  /** 1-based attempt this verdict grades. */
  readonly attempt: number;
  /** Attempts left after this one. */
  readonly attemptsRemaining: number;
  /** Wrong, and attempts remain. The rung is STILL IN PLAY. */
  readonly retry: boolean;
  /** The tier's allowance, echoed so the caller need not look it up again. */
  readonly policy: PinpointAttemptPolicy;
}

/**
 * Grade a rung-1 pinpoint. `latencyMs` must already be the server-clamped value
 * from spec 8.3 — this function scores, it does not clamp, because clamping
 * needs the RTT estimate and that is not the rules package's business.
 *
 * `attempt` defaults to 1, which is the whole of the pre-change-4 behaviour:
 * a tier with a single-attempt policy grades identically to before, and the
 * multiplier for attempt 1 is 1.0 at every tier.
 *
 * A WRONG tap costs nothing directly — it costs the NEXT attempt's multiplier,
 * plus the speed bonus the extra seconds burned. See
 * `SCORING.PINPOINT_ATTEMPT_MULTIPLIERS` for the measured table showing that
 * three rapid adjacent guesses land well below one considered guess.
 */
export function scorePinpoint(args: {
  plan: RoundPlan;
  pickedCca3: string | null;
  latencyMs: number;
  tier: Tier;
  /** 1-based. Defaults to 1. */
  attempt?: number;
}): PinpointVerdict {
  const { plan, pickedCca3, latencyMs, tier } = args;
  const policy = pinpointAttemptPolicy(tier);
  const attempt = args.attempt ?? 1;
  const attemptsRemaining = Math.max(0, policy.maxAttempts - attempt);

  const correct = pickedCca3 !== null && pickedCca3.toUpperCase() === plan.cca3.toUpperCase();
  if (!correct) {
    return {
      correct: false,
      points: 0,
      attempt,
      attemptsRemaining,
      retry: attemptsRemaining > 0,
      policy,
    };
  }
  return {
    correct: true,
    points: pinpointAward(latencyMs, pinpointAttemptMultiplier(policy, attempt)),
    attempt,
    attemptsRemaining,
    retry: false,
    policy,
  };
}

function rungAt(plan: RoundPlan, rung: number): RungPlan {
  const r = plan.rungs[rung - 1];
  if (!r) throw new RangeError(`round ${plan.index} has no rung ${rung}`);
  if (r.rung !== rung) throw new Error(`round ${plan.index} rung ${rung} is misindexed`);
  return r;
}

// ---------------------------------------------------------------------------
// Rungs 2..N — probes
// ---------------------------------------------------------------------------

export interface ProbeVerdict {
  readonly correct: boolean;
  /**
   * Only for an open-numeric rung (change 6). `'exact'` pays the golden
   * multiplier, `'near'` pays the rung's NORMAL multiplier, `'miss'` busts.
   * Undefined for every other answer kind.
   */
  readonly band?: NumericCreditBand;
}

/**
 * Grade a rung-2+ probe against the plan's answer key.
 *
 * The submission is an `AnswerValue`, tagged, so the grader's dispatch is
 * total: an index for choice / boolean / ordering, a number for the open
 * numeric rung. A kind mismatch — a number submitted to a multiple choice, or
 * an index submitted to the open rung — is simply WRONG, not an exception:
 * a malformed submission must not be able to crash a live round.
 *
 * For an ordering probe the index is the permutation's rank in the factorial
 * number system (see `encodeOrdering`).
 */
export function scoreProbe(args: {
  plan: RoundPlan;
  rung: number;
  answer: AnswerValue;
}): ProbeVerdict {
  const r = rungAt(args.plan, args.rung);
  const { answer } = args;

  if (r.answerKind === 'numeric') {
    if (answer.kind !== 'numeric') return { correct: false, band: 'miss' };
    if (typeof r.correctValue !== 'number' || r.numeric === undefined) {
      throw new Error(`round ${args.plan.index} rung ${args.rung} has no numeric answer key`);
    }
    const band = numericCreditBand(r.numeric, r.correctValue, answer.value);
    return { correct: band !== 'miss', band };
  }

  if (answer.kind !== 'index') return { correct: false };
  const optionIdx = answer.optionIdx;

  if (r.correctOrder) {
    const submitted = decodeOrdering(optionIdx, r.correctOrder.length);
    if (!submitted) return { correct: false };
    const correct =
      submitted.length === r.correctOrder.length &&
      submitted.every((v, i) => v === r.correctOrder?.[i]);
    return { correct };
  }

  if (typeof r.correctIndex !== 'number') {
    throw new Error(`round ${args.plan.index} rung ${args.rung} has no answer key`);
  }
  return { correct: optionIdx === r.correctIndex };
}

/**
 * Strip a rung to what the client may see. INVARIANT 5 — this is the only
 * sanctioned path from a RungPlan to the wire.
 *
 * Built by explicit construction rather than by deleting keys: an added
 * answer-bearing field on RungPlan then fails to appear here by default, which
 * is the failure mode you want. `correctValue` and `statementIsTrue` are the
 * two newest proofs that this is the right shape — neither needed a line here
 * to be excluded.
 */
export function publicRound(plan: RoundPlan, rung: number): PublicRung {
  const r = rungAt(plan, rung);

  const out: {
    rung: number;
    probeType: PublicRung['probeType'];
    answerKind: PublicRung['answerKind'];
    prompt: string;
    options?: readonly PublicOption[];
    markers?: readonly LngLat[];
    names?: readonly string[];
    numeric?: PublicRung['numeric'];
    golden?: boolean;
  } = {
    rung: r.rung,
    probeType: r.probeType,
    answerKind: r.answerKind,
    prompt: r.prompt,
  };

  // Two fields, listed by name. An option is `id` and `label` and there is no
  // branch that can add a third, so `imageCca3` — the flag rung's answer key —
  // cannot reach a client even if a future edit forgets it exists.
  if (r.options) {
    out.options = r.options.map((o) => ({ id: o.id, label: o.label }));
  }
  /*
   * A map choice is split rather than copied. The dots go out in option order
   * because that is the answer space the player taps in; the names go out
   * SORTED, so that the two arrays cannot be zipped back into "the capital is
   * at these coordinates". `.sort()` with no comparator is code-unit order,
   * which is the same on every platform — a locale-aware collator is not, and
   * both seats must build byte-identical payloads (spec 8.2).
   */
  if (r.markers) {
    out.markers = r.markers.map((m) => m.lngLat);
    out.names = r.markers.map((m) => m.label).sort();
  }
  // The numeric SPEC is public by design and answer-independent by contract:
  // min, max, step and tolerance are constants of the quantity. See the
  // invariant-5 note on `NumericAnswerSpec`.
  if (r.numeric) out.numeric = r.numeric;
  if (r.golden === true) out.golden = true;

  return out;
}

/**
 * SERVER-ONLY. The country codes whose artwork this rung's options show, in
 * OPTION ORDER, or an empty array for a rung that shows no images.
 *
 * This is the other half of `publicRound`: the public rung says an option
 * exists, and this says which bytes the gateway must seal into that option's
 * slot. It is the answer key for a flag rung in the plainest possible form, so
 * the only caller allowed is one that turns it into opaque tokens — the
 * gateway's `armRound`, and the client's own in-process local driver, which
 * already holds the whole plan.
 */
export function roundImageAssets(plan: RoundPlan, rung: number): readonly string[] {
  const r = rungAt(plan, rung);
  if (!r.options) return [];
  const codes = r.options.map((o) => o.imageCca3);
  if (codes.some((c) => c === undefined)) return [];
  return codes as readonly string[];
}

// ---------------------------------------------------------------------------
// The correct-answer reveal (change 3)
// ---------------------------------------------------------------------------

/**
 * Build the reveal for a rung that has just been RESOLVED.
 *
 * This is the single sanctioned bridge from an answer key to a client, and it
 * is the counterpart of `publicRound`: that function exists to keep answers
 * off the wire while a rung is live, this one exists to put them on it the
 * moment the rung is dead. Both are narrow on purpose.
 *
 * SAFETY. `verdict.final` is the literal `true`, so the caller has to have
 * narrowed a "still in play?" test before it can even call this — a pinpoint
 * with attempts remaining has no `RungVerdict` to hand over. The value it
 * returns is nominally typed and can only be attached to the RESOLVED variant
 * of `rung.result`; the retry variant declares `reveal?: never`. See the long
 * comment on `AnswerReveal` in `@geogame/shared`.
 *
 * `submitted` is what the player sent, or null when they timed out. It travels
 * back with the truth so the reveal card can show the wrong pick in red beside
 * the right one in green without the client having to remember.
 */
export function revealForResolvedRung(args: {
  plan: RoundPlan;
  rung: number;
  verdict: RungVerdict;
  submitted: AnswerValue | null;
  /** Where the player's last pinpoint tap landed. Rung 1 only. */
  pickedLngLat?: LngLat | null;
  /** What the player's last pinpoint tap snapped to. Rung 1 only. */
  pickedCca3?: string | null;
}): AnswerReveal {
  return sealReveal(args.verdict, revealBodyFor(args));
}

/**
 * The reveal's payload, unsealed. Exported for tests and for the client's
 * in-process local driver, which owns both sides and needs no proof.
 */
export function revealBodyFor(args: {
  plan: RoundPlan;
  rung: number;
  submitted: AnswerValue | null;
  pickedLngLat?: LngLat | null;
  pickedCca3?: string | null;
}): RevealBody {
  const { plan, rung, submitted } = args;
  const r = rungAt(plan, rung);
  const idx = submitted?.kind === 'index' ? submitted.optionIdx : null;

  switch (r.answerKind) {
    case 'pinpoint': {
      const target = byCca3(plan.cca3);
      // A country with no baked record cannot be a target, so this is
      // unreachable; falling back to [0,0] keeps a data gap from throwing
      // inside a live round.
      const latlng = target?.latlng ?? [0, 0];
      const targetLngLat: LngLat = plan.targetLngLat ?? [latlng[1], latlng[0]];
      return {
        kind: 'pinpoint',
        rung,
        correctCca3: plan.cca3,
        correctLngLat: targetLngLat,
        ...(plan.targetName ? { correctLabel: plan.targetName } : {}),
        pickedCca3: args.pickedCca3 ?? null,
        pickedLngLat: args.pickedLngLat ?? null,
      };
    }
    case 'boolean':
      return {
        kind: 'boolean',
        rung,
        correctIndex: r.correctIndex ?? -1,
        pickedIndex: idx,
        statementIsTrue: r.statementIsTrue === true,
        ...(r.statementFact ? { correction: r.statementFact } : {}),
      };
    case 'ordering':
      return {
        kind: 'ordering',
        rung,
        correctOrder: r.correctOrder ?? [],
        pickedOrder:
          idx === null || r.correctOrder === undefined
            ? null
            : decodeOrdering(idx, r.correctOrder.length),
      };
    case 'numeric': {
      const value = submitted?.kind === 'numeric' ? submitted.value : null;
      const correctValue = r.correctValue ?? 0;
      const band =
        value === null || r.numeric === undefined
          ? 'miss'
          : numericCreditBand(r.numeric, correctValue, value);
      return {
        kind: 'numeric',
        rung,
        quantity: r.numeric?.quantity ?? 'un-year',
        correctValue,
        pickedValue: value,
        band,
      };
    }
    case 'choice':
    default:
      return {
        kind: 'choice',
        rung,
        correctIndex: r.correctIndex ?? -1,
        pickedIndex: idx,
        // The map choice's dots got to the client without their names; this is
        // where the two halves are re-joined, and the rung is dead by now.
        ...(r.markers ? { labels: r.markers.map((m) => m.label) } : {}),
      };
  }
}

// ---------------------------------------------------------------------------
// Ordering probes over an integer-only wire
// ---------------------------------------------------------------------------

function factorial(n: number): number {
  let f = 1;
  for (let i = 2; i <= n; i += 1) f *= i;
  return f;
}

/**
 * Rank a permutation in the factorial number system (Lehmer code).
 *
 * `answer { kind: 'index' }` carries one integer, so an ordering submission is
 * sent as its rank. Five rows is the ceiling for an ordering probe, so the rank
 * is at most 119 — comfortably an int, and no protocol change is needed to
 * support ordering at all.
 */
export function encodeOrdering(perm: readonly number[]): number {
  const n = perm.length;
  const seen = new Set(perm);
  if (seen.size !== n || perm.some((v) => !Number.isInteger(v) || v < 0 || v >= n)) {
    throw new RangeError('encodeOrdering: not a permutation of 0..n-1');
  }

  const pool = Array.from({ length: n }, (_, i) => i);
  let rank = 0;
  for (let i = 0; i < n; i += 1) {
    const idx = pool.indexOf(perm[i] as number);
    rank += idx * factorial(n - 1 - i);
    pool.splice(idx, 1);
  }
  return rank;
}

/** Inverse of `encodeOrdering`. Returns null when the rank is out of range. */
export function decodeOrdering(rank: number, n: number): number[] | null {
  if (!Number.isInteger(rank) || rank < 0 || n < 0) return null;
  if (rank >= factorial(n)) return null;

  const pool = Array.from({ length: n }, (_, i) => i);
  const out: number[] = [];
  let rest = rank;
  for (let i = 0; i < n; i += 1) {
    const f = factorial(n - 1 - i);
    const idx = Math.floor(rest / f);
    rest %= f;
    const [taken] = pool.splice(idx, 1);
    if (taken === undefined) return null;
    out.push(taken);
  }
  return out;
}
